import { useState, type MouseEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { useApp } from "@/hooks/use-app-context";
import { LanguageSelector } from "@/components/ui/language-selector";
import { cn } from "@/lib/utils";
import { HeaderThemeToggle } from "./HeaderThemeToggle";
import { SiteHeaderBar } from "./SiteHeaderBar";
import { VisitorMobileMenu } from "./VisitorMobileMenu";
import { visitorHeaderText } from "./visitor-header-copy";
import { isHomePath, scrollToSection, visitorNav, type VisitorNavItem } from "./visitor-nav";

const linkClass =
  "inline-flex h-10 items-center whitespace-nowrap rounded-full px-3.5 text-sm font-medium transition-colors hover:bg-muted/60 hover:text-foreground focus-ring";

/**
 * Header of the public website for a visitor: a few links into the home page,
 * then language, theme, sign in and sign up. Sign up stays visible at every
 * width; the links move into the menu below 1024 px.
 */
export function VisitorHeader({ lang }: { lang: string }) {
  const { t, language } = useApp();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const copy = visitorHeaderText(language);
  const items = visitorNav(lang, language);
  const onHome = isHomePath(pathname, lang);

  // On the home page a section link scrolls in place instead of navigating.
  const followSection = (item: VisitorNavItem) => (event: MouseEvent) => {
    setMenuOpen(false);
    if (item.section && onHome && scrollToSection(item.section)) {
      event.preventDefault();
      window.history.replaceState(null, "", `${pathname}#${item.section}`);
    }
  };

  const nav = (
    <nav className="hidden items-center gap-0.5 lg:flex" aria-label={copy.primaryNav}>
      {items.map((item) => {
        const active = !item.section && pathname.startsWith(item.href);
        return (
          <Link
            key={item.href}
            to={item.href}
            onClick={followSection(item)}
            aria-current={active ? "page" : undefined}
            className={cn(linkClass, active ? "text-foreground" : "text-muted-foreground")}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );

  const actions = (
    <>
      <LanguageSelector variant="header" className="hidden md:inline-flex" />
      <HeaderThemeToggle />
      <span aria-hidden="true" className="mx-1 hidden h-5 w-px bg-border sm:block" />
      <Link to={`/${lang}/login`} className={cn(linkClass, "hidden text-foreground min-[400px]:inline-flex")}>
        {t.login}
      </Link>
      <Link
        to={`/${lang}/signup`}
        className="inline-flex h-10 items-center whitespace-nowrap rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-ring"
      >
        {t.signup}
      </Link>
      <button
        type="button"
        onClick={() => setMenuOpen((open) => !open)}
        aria-label={menuOpen ? copy.closeMenu : copy.openMenu}
        aria-expanded={menuOpen}
        className="grid size-10 shrink-0 place-items-center rounded-full text-foreground transition-colors hover:bg-muted/60 focus-ring lg:hidden"
      >
        <span aria-hidden="true" className="flex w-[18px] flex-col gap-[5px]">
          <span className="h-[1.5px] rounded-full bg-current" />
          <span className="h-[1.5px] rounded-full bg-current" />
          <span className="h-[1.5px] rounded-full bg-current" />
        </span>
      </button>
    </>
  );

  return (
    <>
      <SiteHeaderBar lang={lang} nav={nav} actions={actions} />
      <VisitorMobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        lang={lang}
        items={items}
        onFollow={followSection}
      />
    </>
  );
}
